/**
 * Oncology Service
 * 
 * Provides API methods for oncology module operations including
 * cancer diagnoses, clinical assessments, and oncology dashboard statistics.
 */

import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get authorization headers for API requests
 */
const getAuthHeaders = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Handle API response
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || 'API request failed');
  }
  return response.json();
};

// =============================================================================
// BSA & DOSE CALCULATIONS
// =============================================================================

/**
 * Calculate Body Surface Area (BSA)
 * @param {number} heightCm - Height in centimeters
 * @param {number} weightKg - Weight in kilograms
 * @param {string} formula - BSA formula to use (Mosteller, DuBois, Haycock, Gehan-George, Boyd)
 * @returns {Promise<{bsaValue: number, formula: string, heightCm: number, weightKg: number}>}
 */
export const calculateBSA = async (heightCm, weightKg, formula = 'Mosteller') => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/calculate-bsa`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ heightCm, weightKg, formula })
  });
  return handleResponse(response);
};

/**
 * Calculate chemotherapy dose based on BSA
 * @param {Object} params - Dose calculation parameters
 * @param {number} params.bsaValue - Body Surface Area in m²
 * @param {number} params.standardDose - Standard dose from protocol
 * @param {string} params.doseUnit - Dose unit (e.g., mg/m², mg/kg, mg)
 * @param {number} [params.weightKg] - Weight in kg (required for mg/kg dosing)
 * @param {number} [params.maxDose] - Maximum dose cap
 * @param {number} [params.reductionPercent] - Dose reduction percentage
 * @returns {Promise<{calculatedDose: number, doseUnit: string, bsaUsed: number, cappedAtMax: boolean}>}
 */
export const calculateDose = async (params) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/calculate-dose`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params)
  });
  return handleResponse(response);
};

// =============================================================================
// CANCER DIAGNOSES
// =============================================================================

/**
 * Get cancer diagnoses for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.status] - Filter by status
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Items per page
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getCancerDiagnoses = async (patientId, facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.status) params.append('status', options.status);

  const response = await fetch(`${API_BASE}/api/oncology/patients/${patientId}/diagnoses?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Get all cancer diagnoses for a facility (facility-wide view)
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.status] - Filter by status
 * @param {string} [options.cancerType] - Filter by cancer type
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Items per page
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getAllCancerDiagnoses = async (facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.status) params.append('status', options.status);
  if (options.cancerType) params.append('cancerType', options.cancerType);

  const response = await fetch(`${API_BASE}/api/oncology/diagnoses?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Get a single cancer diagnosis by ID
 * @param {string} diagnosisId - Diagnosis ID
 * @returns {Promise<Object>}
 */
export const getCancerDiagnosis = async (diagnosisId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/diagnoses/${diagnosisId}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a new cancer diagnosis
 * @param {Object} diagnosisData - Diagnosis data
 * @param {string} diagnosisData.patientId - Patient ID
 * @param {string} diagnosisData.facilityId - Facility ID
 * @param {string} diagnosisData.cancerType - Cancer type
 * @param {string} diagnosisData.primarySite - Primary site
 * @param {string} diagnosisData.histology - Histology description
 * @param {Object} diagnosisData.staging - TNM staging object
 * @param {string} diagnosisData.diagnosisDate - Diagnosis date
 * @param {string} [diagnosisData.encounterId] - Encounter ID where diagnosis was made
 * @returns {Promise<Object>}
 */
export const createCancerDiagnosis = async (diagnosisData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/diagnoses`, {
    method: 'POST',
    headers,
    body: JSON.stringify(diagnosisData)
  });
  return handleResponse(response);
};

/**
 * Update a cancer diagnosis
 * @param {string} diagnosisId - Diagnosis ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>}
 */
export const updateCancerDiagnosis = async (diagnosisId, updateData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/diagnoses/${diagnosisId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updateData)
  });
  return handleResponse(response);
};

// =============================================================================
// CLINICAL ASSESSMENTS
// =============================================================================

/**
 * Get clinical assessments (ECOG, Karnofsky) for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getClinicalAssessments = async (patientId, facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });

  const response = await fetch(`${API_BASE}/api/oncology/patients/${patientId}/assessments?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a clinical assessment
 * @param {Object} assessmentData - Assessment data
 * @param {string} assessmentData.patientId - Patient ID
 * @param {string} assessmentData.facilityId - Facility ID
 * @param {string} [assessmentData.ecogStatus] - ECOG performance status (0-5)
 * @param {string} [assessmentData.karnofskyScore] - Karnofsky score (0-100)
 * @param {string} [assessmentData.encounterId] - Associated encounter ID
 * @returns {Promise<Object>}
 */
export const createClinicalAssessment = async (assessmentData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/assessments`, {
    method: 'POST',
    headers,
    body: JSON.stringify(assessmentData)
  });
  return handleResponse(response);
};

// =============================================================================
// TREATMENT PLANS
// =============================================================================

/**
 * Get treatment plans for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getTreatmentPlans = async (patientId, facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.status) params.append('status', options.status);

  const response = await fetch(`${API_BASE}/api/oncology/patients/${patientId}/treatment-plans?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Get a single treatment plan
 * @param {string} planId - Treatment plan ID
 * @returns {Promise<Object>}
 */
export const getTreatmentPlan = async (planId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/treatment-plans/${planId}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a treatment plan
 * @param {Object} planData - Treatment plan data
 * @returns {Promise<Object>}
 */
export const createTreatmentPlan = async (planData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/treatment-plans`, {
    method: 'POST',
    headers,
    body: JSON.stringify(planData)
  });
  return handleResponse(response);
};

/**
 * Update a treatment plan
 * @param {string} planId - Treatment plan ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>}
 */
export const updateTreatmentPlan = async (planId, updateData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/treatment-plans/${planId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updateData)
  });
  return handleResponse(response);
};

// =============================================================================
// SURVIVORSHIP CARE PLANS
// =============================================================================

/**
 * Get survivorship plans for a patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getSurvivorshipPlans = async (patientId, facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.status) params.append('status', options.status);

  const response = await fetch(`${API_BASE}/api/oncology/patients/${patientId}/survivorship-plans?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Get all survivorship plans for a facility (facility-wide list view)
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.search] - Search term for patient name or diagnosis
 * @param {string} [options.status] - Filter by status
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Items per page
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getAllSurvivorshipPlans = async (facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.search) params.append('search', options.search);
  if (options.status) params.append('status', options.status);

  const response = await fetch(`${API_BASE}/api/oncology/survivorship-plans?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Get a single survivorship plan by ID
 * @param {string} planId - Survivorship plan ID
 * @returns {Promise<Object>}
 */
export const getSurvivorshipPlan = async (planId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/survivorship-plans/${planId}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a survivorship care plan
 * @param {Object} planData - Survivorship plan data
 * @param {string} planData.facilityId - Facility ID
 * @param {string} planData.patientId - Patient ID
 * @param {string} planData.diagnosisId - Diagnosis ID
 * @param {string} [planData.encounterId] - Encounter ID where plan was created
 * @param {string} [planData.episodeOfCareId] - Episode of care ID
 * @param {Object} [planData.treatmentSummary] - Summary of treatments received
 * @param {Array} [planData.surveillanceSchedule] - Surveillance test schedule
 * @param {Array} [planData.potentialLateEffects] - Potential late effects to monitor
 * @param {Array} [planData.healthMaintenance] - Health maintenance recommendations
 * @param {Array} [planData.followUpProviders] - Follow-up care providers
 * @returns {Promise<Object>}
 */
export const createSurvivorshipPlan = async (planData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/survivorship-plans`, {
    method: 'POST',
    headers,
    body: JSON.stringify(planData)
  });
  return handleResponse(response);
};

/**
 * Update a survivorship care plan
 * @param {string} planId - Survivorship plan ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>}
 */
export const updateSurvivorshipPlan = async (planId, updateData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/survivorship-plans/${planId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updateData)
  });
  return handleResponse(response);
};

// =============================================================================
// ONCOLOGY DASHBOARD
// =============================================================================

/**
 * Get oncology statistics for dashboard
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>}
 */
export const getOncologyStats = async (facilityId) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ facilityId });
  const response = await fetch(`${API_BASE}/api/oncology/stats?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

// =============================================================================
// ENCOUNTER-BASED QUERIES
// =============================================================================

/**
 * Get all oncology records for a specific encounter
 * 
 * This retrieves all oncology-related activities that occurred during a specific encounter,
 * which is useful for displaying oncology context within encounter documentation.
 * 
 * @param {string} encounterId - Encounter ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<{
 *   encounterId: string,
 *   diagnoses: Array,
 *   assessments: Array,
 *   toxicityRecords: Array,
 *   chemotherapyCycles: Array,
 *   survivorshipPlans: Array
 * }>}
 */
export const getOncologyRecordsForEncounter = async (encounterId, facilityId) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ facilityId });
  const response = await fetch(`${API_BASE}/api/oncology/encounters/${encounterId}/oncology-records?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

const oncologyService = {
  // BSA & Dose Calculations
  calculateBSA,
  calculateDose,
  // Cancer Diagnoses
  getCancerDiagnoses,
  getAllCancerDiagnoses,
  getCancerDiagnosis,
  createCancerDiagnosis,
  updateCancerDiagnosis,
  // Clinical Assessments
  getClinicalAssessments,
  createClinicalAssessment,
  // Treatment Plans
  getTreatmentPlans,
  getTreatmentPlan,
  createTreatmentPlan,
  updateTreatmentPlan,
  // Survivorship
  getSurvivorshipPlans,
  getAllSurvivorshipPlans,
  getSurvivorshipPlan,
  createSurvivorshipPlan,
  updateSurvivorshipPlan,
  // Dashboard
  getOncologyStats,
  // Encounter-based queries
  getOncologyRecordsForEncounter
};

export default oncologyService;

